// What the SERVER knows about a device found in a Bluetooth scan (2026-10-18).
//
// A device on the bench has no Wi-Fi yet, but the phone scanning it does — so
// the phone looks each scanned unique number up itself
// (deviceAdmin.findDevice -> GetDeviceDetailbyUniqueNumber, fresh, never the
// local cache: these are other people's devices). The row then says what the
// board IS and where it belongs before anyone connects: type, owner, location,
// room, name, Reverse action — and an unassigned one can be assigned from here
// (AssignDeviceButton, the same dialog as the Message Box).
//
// ⚠️ It shows OWNER and LOCATION, so it lives only on the Super-Admin BLE Test
// screen. Do not drop it into customer onboarding without narrowing it.
//
// Every scanned number is looked up at once, no cap; once per number per screen
// visit (cached in a ref). `refresh(uno)` re-reads one after an assign.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableBody,
  TableRow, TableCell, Alert, CircularProgress, Box,
} from '@mui/material';
import { findDevice } from '../../../core/devices/deviceAdmin';
import AssignDeviceButton from '../../shared/AssignDeviceButton';
import { FIT_SM } from '../../shared/fitDialog';

/**
 * -> { info, refresh }
 *    info: { [uno]: { loading } | { found:false } | { found, assigned, device } | { error } }
 */
export function useServerDetails(uniqueNumbers) {
  const [info, setInfo] = useState({});
  const asked = useRef(new Set());
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const fetchOne = useCallback(async (uno) => {
    setInfo((m) => ({ ...m, [uno]: { ...(m[uno] || {}), loading: true } }));
    const r = await findDevice(uno).catch(() => ({ error: true }));
    if (alive.current) setInfo((m) => ({ ...m, [uno]: r || { error: true } }));
  }, []);

  const key = (uniqueNumbers || []).filter(Boolean).join(',');
  useEffect(() => {
    const todo = key ? key.split(',').filter((u) => !asked.current.has(u)) : [];
    if (!todo.length) return;
    todo.forEach((u) => asked.current.add(u));
    // All at once — no cap (owner, 2026-10-18: a 25-board install).
    todo.forEach(fetchOne);
  }, [key, fetchOne]);

  return { info, refresh: fetchOne };
}

const yesNo = (v) => (Number(v) ? 'Yes' : 'No');
const when = (v) => {
  const t = v ? Date.parse(v) : NaN;
  if (!Number.isFinite(t) || t < Date.UTC(2000, 0, 1)) return '—';
  return new Date(t).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

/** The one line under the unique number in the scan list. */
export function serverSummary(r) {
  if (!r || r.loading) return 'server: looking up…';
  if (r.error) return 'server: lookup failed';
  if (!r.found) return 'server: not registered';
  const d = r.device || {};
  const type = d.DeviceTypeName ? `${d.DeviceTypeName} (${d.DeviceID})` : `type ${d.DeviceID}`;
  if (!r.assigned) return `${type} · UNASSIGNED`;
  const room = d.RoomName || (Number(d.RoomId) === 0 ? 'Common' : '');
  return `${type} · ${[d.LocationName, room, d.DeviceName].filter(Boolean).join(' / ')}`;
}

/** Everything the server holds that matters on the bench, plus Assign. */
export function BleServerInfoDialog({
  uno, result, onClose, onRefresh,
}) {
  const d = result?.device || {};
  const rows = result?.found ? [
    ['Device type', `${d.DeviceTypeName || '—'} · DeviceID ${d.DeviceID ?? '—'}`],
    ['Device name', d.DeviceName || '—'],
    ['Owner', result.assigned
      ? `${d.DeviceMasterUser || d.DeviceMasterUserName || `User ${d.MasterUserId}`}${d.MasUserMobileNumber ? ` · ${d.MasUserMobileNumber}` : ''}`
      : '— (unassigned)'],
    ['Location', d.LocationName || '—'],
    ['Room', d.RoomName || (result.assigned && Number(d.RoomId) === 0 ? 'Common device' : '—')],
    ['Mapping', d.DeviceMappingStatus || '—'],
    ['Reverse action', yesNo(d.ReverseAction)],
    ['Outputs', `${d.Outputs ?? '—'} (switches ${d.On_off ?? 0}, dimming ${d.Dimming ?? 0})`],
    ['Alexa type', d.AlexaType || '—'],
    ['Device lock', yesNo(d.DeviceLock)],
    ['Wi-Fi joined', d.WifiName || '—'],
    ['Last seen (MQTT)', when(d.LastMqttPing)],
    ['Firmware file', d.BinFileName ? `${d.BinFileName} #${d.OTABinFileId}` : '—'],
    ['Code updated', when(d.CodeUpdated)],
    ['Installed row ID', d.ID ?? '—'],
  ] : [];

  return (
    <Dialog open={!!uno} onClose={onClose} {...FIT_SM}>
      <DialogTitle sx={{ pb: 0.5 }}>{`${uno} — on the server`}</DialogTitle>
      <DialogContent>
        {(!result || result.loading) && <Box sx={{ textAlign: 'center', p: 2 }}><CircularProgress size={22} /></Box>}
        {result?.error && <Alert severity="error">Could not reach the server — check the phone’s internet.</Alert>}
        {result && !result.loading && !result.error && !result.found && (
          <Alert severity="warning">
            {`${uno} has no record on the server — it was never registered, so it cannot be assigned yet.`}
          </Alert>
        )}
        {result?.found && (
          <>
            <Alert severity={result.assigned ? 'info' : 'success'} sx={{ mb: 1, py: 0 }}>
              {result.assigned ? 'Assigned.' : 'Unassigned — ready to assign.'}
            </Alert>
            <Table size="small">
              <TableBody>
                {rows.map(([k, v]) => (
                  <TableRow key={k}>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', py: 0.5 }}>{k}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{v}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {result?.found && !result.assigned && (
          <AssignDeviceButton uniqueNumber={uno} onDone={() => onRefresh(uno)} />
        )}
        <Button onClick={() => onRefresh(uno)} disabled={result?.loading}>Refresh</Button>
        <Button variant="contained" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
